import { h, Component } from 'preact';
import { joinClasses, debounce } from 'utils/utils';
import { createNewTicket, setTicketStatus } from 'utils/tickets';
import { showAddGuestModal } from 'utils/addGuestModal';

import TheaterInfo from 'components/theaterInfo/theaterInfo';
import CapacityInfo from 'components/capacityInfo/capacityInfo';
import IconSearch from 'components/icons/search';
import IconEdit from 'components/icons/edit';
import IconTick from 'components/icons/tick';

import s from 'components/search/search.scss';
import { trackGAEvent } from 'utils/track';

// Levenstein of substring distance (based on http://ginstrom.com/scribbles/2007/12/01/fuzzy-substring-matching-with-levenshtein-distance-in-python/)
// s1: needle, s2: haystack (s2 bigger than s1)
function subLev(s1, s2) {
	s1 = (s1 + '').toLowerCase();
	s2 = (s2 + '').toLowerCase();
	let m = s1.length;
	let n = s2.length;
	if (m === 1) {
		return !~s2.indexOf(s1);
	}
	if (!n) {
		return m;
	}
	let r1 = new Array(n + 1).fill(0);
	for (let i = 0; i < m; i++) {
		let r2 = [i + 1];
		for (let j = 0; j < n; j++) {
			let cost = +(s1[i] != s2[j]);
			r2.push(Math.min(r1[j + 1] + 1, r2[j] + 1, r1[j] + cost));
		}
		r1 = r2;
	}
	return Math.min(...r1);
}
if (typeof window !== 'undefined') window.lev = subLev;

const statusCopy = {
	cancelled: 'cancelled'
};

export default class Search extends Component {
	constructor(props) {
		super(props);
		this.onFormSubmit = this.onFormSubmit.bind(this);
		this.onSearchInput = this.onSearchInput.bind(this);
		this.search = this.search.bind(this);
		this.addGuest = this.addGuest.bind(this);
		this.toggleStatusEdition = this.toggleStatusEdition.bind(this);
		this.maybeCloseStatusEditor = this.maybeCloseStatusEditor.bind(this);
		this.trackSearch = debounce(this.trackSearch, 1000);

		this.state = {
			results: []
		};
	}

	componentDidMount() {
		document.addEventListener('click', this.maybeCloseStatusEditor);

		const searchInput = this.base.querySelector('input[name="search"]');
		searchInput.value = '';
		searchInput.focus();
	}

	componentWillReceiveProps(newProps) {
		if (newProps.tickets !== this.props.tickets) {
			this.search(newProps);
		}
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.maybeCloseStatusEditor);
	}

	maybeCloseStatusEditor(e) {
		const inActions = e.target?.closest(`.${s.actions}`);
		if (!inActions) {
			this.setState({ openedStatusEditor: null });
		}
	}

	onFormSubmit(e) {
		e.preventDefault();
		this.search();
	}

	trackSearch() {
		const data = new FormData(this.$search);
		const search = data.get('search')?.trim().toUpperCase();
		trackGAEvent('searchNameInput', 'search', `${search}`);
	}

	onSearchInput() {
		this.search();

		this.trackSearch();
	}

	search(props = this.props) {
		const data = new FormData(this.$search);
		const search = data.get('search')?.trim().toUpperCase();
		if (search.length < 3) {
			return this.setState({ search, results: [] });
		}
		let all = props.tickets;
		let acceptableLevDist = Math.max(Math.min(3, search.length / 3), 0);
		const results = all?.map(ticket => {
			if (ticket.shortcode === search) {
				return { ticket, score: 4 };
			}
			const name = ticket.name.toUpperCase();
			if (name.startsWith(search)) {
				return { ticket, score: 3 };
			}
			if (name.includes(search)) {
				return { ticket, score: 2 };
			}
			const dist = subLev(search, ticket.name.toUpperCase());
			if (dist <= acceptableLevDist) {
				return { ticket, score: 1 / (dist + 1) };
			}
			return false;
		}).filter(e => e).sort((a, b) => (
			b.score - a.score || a.ticket.name.localeCompare(b.ticket.name)
		)).map(r => r.ticket.id) || [];
		this.setState({ search, results });
		trackGAEvent('search', 'tickets', '');
	}

	nothing(e) {
		e.preventDefault();
	}

	async addGuest() {
		trackGAEvent('add-guest', 'addGuest', '');
		const data = await showAddGuestModal();
		if (!data || !data.name || !data.count) {
			return;
		}
		if (data.name)
			trackGAEvent('add-guest-name-input', 'addGuest', data.name);
		if (data.count)
			trackGAEvent('add-guest-ticket-qty-input', 'addGuest', data.count);
		if (data.type)
			trackGAEvent('add-guest-category-select', 'addGuest', data.type);
		await createNewTicket(data);
	}

	toggleStatusEdition(result) {
		this.setState({ openedStatusEditor: this.state.openedStatusEditor === result ? null : result });
	}

	updateStatus(result, status) {
		trackGAEvent('searchEditTicketStatus', 'search', status);
		setTicketStatus(result.id, status);
		this.setState({ openedStatusEditor: null });
	}

	render(props, state) {
		const { theater, movie, screening, tickets, offline } = props;
		const { search, results, openedStatusEditor } = state;

		const displayedResults = results.map(result => tickets.find(t => t.id === result));
		const renderedRows = displayedResults.length || (search ? 1 : 0);

		return (
			<div class={s.root}>
				<TheaterInfo theater={theater} movie={movie} screening={screening} mini class={s.theaterInfo} />
				<form class={s.search} onSubmit={this.onFormSubmit} ref={e => this.$search = e}>
					<label>
						<input class={s.searchField} type="text" name="search" placeholder="Name or code" onInput={this.onSearchInput} autofocus />
						<button class={s.searchSubmit} type="submit"><IconSearch /></button>
					</label>
				</form>
				<div class={s.resultsContainer}>
					<table class={s.results}>
						<thead>
							<tr>
								<th>
									<span>Name</span><button className={s.addGuest} onClick={this.addGuest}><div class={s.icon} /><span>ADD GUEST</span></button>
								</th>
								<th>Status</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{!!search && !displayedResults.length && <tr class={s.empty}><td colSpan={3}>{search.length < 3 ? 'Please type at least 3 characters' : 'No results'}</td></tr>}
							{displayedResults.map(result => (
								<tr key={result.id || result.shortcode}>
									<td class={s.name}>{result.name}</td>
									<td class={joinClasses(s.status, s[result.status])}>{statusCopy[result.status] || result.status}</td>
									<td class={s.actions}>
										<form class={joinClasses(s.statusEdition, s[result.status], openedStatusEditor === result && s.show)} onSubmit={this.nothing}>
											{['admitted', 'absent', 'cancelled'].map(status => (
												<label class={joinClasses(s.statusSelector, s[status], status === result.status && s.active)}>
													<input
														type="radio"
														name="status"
														checked={status === result.status}
														onChange={() => this.updateStatus(result, status)}
													/>
													<IconTick class={s.indicator} />
													<span class={s.name}>{statusCopy[status] || status}</span>
												</label>
											))}
										</form>
										<button class={s.edit} onClick={() => this.toggleStatusEdition(result)}>
											<IconEdit />
										</button>
									</td>
								</tr>
							))}
							{[...Array(Math.max(0, 5 - renderedRows))].map(() => <tr class={s.empty}><td colSpan={3} /></tr>)}
						</tbody>
					</table>
				</div>
				<CapacityInfo screening={screening} tickets={tickets} offline={offline} />
			</div>
		);
	}
}